import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, Loader2 } from "lucide-react";

const MAX = 2;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt";

export const ServiceAttachments = ({ job, canEdit, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const atts = job.attachments || [];

  const open = async (a) => {
    try {
      const res = await api.get(`/service-jobs/${job.id}/attachments/${a.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error("Could not open attachment");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) return toast.error("Each attachment must be 5 MB or smaller");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/service-jobs/${job.id}/attachments`, fd);
      toast.success("Attachment added");
      onChange();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove '${a.filename}'?`)) return;
    try {
      await api.delete(`/service-jobs/${job.id}/attachments/${a.id}`);
      toast.success("Attachment removed");
      onChange();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  if (!atts.length && !canEdit) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`service-attachments-${job.id}`}>
      {atts.map((a) => (
        <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-zinc-100 border border-zinc-300 px-2 py-0.5 max-w-[220px]">
          <button onClick={() => open(a)} data-testid={`service-attachment-open-${a.id}`} className="truncate hover:text-brand font-semibold text-zinc-700">{a.filename}</button>
          {canEdit && <button onClick={() => remove(a)} data-testid={`service-attachment-remove-${a.id}`} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>}
        </span>
      ))}
      {canEdit && atts.length < MAX && (
        <>
          <input ref={input} type="file" accept={ACCEPT} onChange={upload} className="hidden" data-testid={`service-attachment-input-${job.id}`} />
          <button onClick={() => input.current?.click()} disabled={busy} data-testid={`service-attachment-add-${job.id}`}
            className="inline-flex items-center gap-1 text-xs font-bold text-zinc-500 hover:text-brand disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />} Attach file <span className="font-normal text-zinc-400">({atts.length}/{MAX}, 5 MB max)</span>
          </button>
        </>
      )}
    </div>
  );
};
